/*
 * THE CLIENT BRIEF'S LINE ON THE CLIENT CARD (src/brief on the server).
 *
 * The brief lives in Slack, as the "Initial Client Brief" canvas in the client's
 * internal channel; this line says where it stands and links to it:
 *   none            "Not written yet."                                 Write client brief
 *   writing         "Writing from the calls and emails…"               (none; the card polls)
 *   ready, in Slack "In #guardz. Written Oct 15 from 1 call and 25 emails."  Open in Slack · Review · Rewrite
 *   ready, not yet  "Written Oct 15 from …. Not in Slack yet."         [Review and post to Slack] · Rewrite
 *                   (after a failed post: "Not in Slack yet: <why>.")
 *
 * Posting is only ever a click, and only after the brief has been read: the
 * yellow button opens the review (ClientBriefReview.js), and Post is at its foot.
 * The server writes a new client's brief on its own but never posts it (Gabriel,
 * 2026-10-15: "only pushed from the CRM", "review it before sending").
 *   failed          "Couldn't be written: <why>."                      Try again
 *
 * Pure, so the wording is tested without React (test/client-brief.test.js).
 */

const ZONE = "Asia/Jerusalem";

function day(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: ZONE });
}

function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

/** "4 calls and 18 emails", "2 calls, 9 emails and 1 note", or null. */
export function readFrom(sources) {
  if (!sources) return null;
  const parts = [
    sources.calls ? plural(sources.calls, "call", "calls") : null,
    sources.emails ? plural(sources.emails, "email", "emails") : null,
    sources.notes ? plural(sources.notes, "note", "notes") : null,
    sources.forms ? plural(sources.forms, "form", "forms") : null,
    sources.messages ? plural(sources.messages, "message", "messages") : null,
  ].filter(Boolean);
  if (!parts.length) return null;
  if (parts.length === 1) return parts[0];
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

/** { text, actions: ["write" | "open" | "view" | "review" | "rewrite" | "retry"], polling, url } */
export function briefLine(status) {
  const s = (status && status.status) || "none";
  if (s === "writing") {
    return { text: "Writing from the calls and emails. Takes a few minutes.", actions: [], polling: true, url: null };
  }
  if (s === "ready") {
    const from = readFrom(status.sources);
    const when = day(status.written_at);
    const written = [when ? `Written ${when}` : "Written", from ? `from ${from}` : null].filter(Boolean).join(" ");
    const slack = status.slack || {};
    if (slack.canvas_url) {
      return { text: `In #${slack.channel}. ${written}.`, actions: ["open", "view", "rewrite"], polling: false, url: slack.canvas_url };
    }
    const why = slack.error ? ` Not in Slack yet: ${String(slack.error).replace(/\.$/, "")}.` : " Not in Slack yet.";
    return { text: `${written}.${why}`, actions: ["review", "rewrite"], polling: false, url: null };
  }
  if (s === "failed") {
    const why = status.error ? `: ${String(status.error).replace(/\.$/, "")}` : "";
    return { text: `Couldn’t be written${why}.`, actions: ["retry"], polling: false, url: null };
  }
  return { text: "Not written yet.", actions: ["write"], polling: false, url: null };
}
